import React, { useState, useMemo, useEffect } from "react";

import { Tab, Tabs, Typography } from "@mui/material";

import { fetchEnvironmentStatus } from "libLegacy/api/dashboard";
import { DeviceStatus, Environment } from "libLegacy/api/models";

import { BlockStandard } from "components/admin/BlockStandard";
import { PageStandard } from "components/admin/PageStandard";
import { ListItemSpaces } from "components/automation/ListItemSpaces";
import { FilteredList } from "components/MUIExtensions/FilteredList";
import { useToken, useUserContext } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";

const formatEnvironments = (environments: Environment[]) => {
  return environments.map((environment) => {
    let onlineDevices = 0;
    let offlineDevices = 0;

    if (environment.devices) {
      environment.devices.forEach((device) => {
        device.status === DeviceStatus.online
          ? onlineDevices++
          : offlineDevices++;
      });
    }
    return { ...environment, onlineDevices, offlineDevices };
  });
};

export const Environments = ({ match }) => {
  const [view, setView] = useState<"all" | "online" | "offline">("offline");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const token = useToken();
  const { currentSpace } = useUserContext();
  const setGlobalError = useSetError();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchEnvironmentStatus(
          token,
          currentSpace?.id || ""
        );
        setEnvironments(formatEnvironments(data));
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    if (currentSpace) {
      load();
    }
  }, [token, currentSpace, setGlobalError]);

  useEffect(() => {
    if (match.params?.status) {
      setView(match.params.status);
    }
  }, [match]);

  const filteredEnvironments = useMemo(() => {
    if (view === "all") {
      return environments;
    }

    // an environment is displayed as online if ALL devices on it are online
    else if (view === "online") {
      return environments.filter((environment) =>
        environment.devices?.every(
          (device) => device.status === DeviceStatus.online
        )
      );
    }

    // an environment is displayed as offline if ANY devices on it are offline
    else if (view === "offline") {
      return environments.filter(
        (environment) =>
          !environment.devices?.every(
            (device) => device.status === DeviceStatus.online
          )
      );
    }
  }, [environments, view]);

  const handleViewChange = (e, newValue) => {
    setView(newValue);
  };

  return (
    <PageStandard title="Ambientes">
      <BlockStandard hideDivider>
        <Tabs textColor="secondary" value={view} onChange={handleViewChange}>
          <Tab label="Desconectados" value="offline" />
          <Tab label="Conectados" value="online" />
          <Tab label="Todos" value="all" />
        </Tabs>
        <FilteredList
          ListItemComponent={ListItemSpaces}
          //@ts-expect-error fetch function isn't using type from Proto yet
          data={filteredEnvironments}
          getKey={(item) => item.name}
          getName={(item) => item.name}
          isLoading={isLoading}
        />
        {environments.length === 0 && !isLoading && (
          <Typography variant="h1" textAlign="center">
            Você ainda não criou ambientes.
          </Typography>
        )}
      </BlockStandard>
    </PageStandard>
  );
};
